import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { subDays, differenceInDays, format, parseISO, isValid } from 'date-fns';
import { MapPin, Building2, BarChart3, Printer, Maximize2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLocation } from '@/contexts/LocationContext';
import { useRole } from '@/hooks/use-role';
import { DashboardHistoryChart } from '@/features/production-orders/components/DashboardHistoryChart';
import { useDashboardHistory } from '@/features/production-orders/hooks/useDashboardHistory';
import {
  DateRangePicker,
  SummaryStatsCards,
  DefectTrendChart,
  DefectBreakdownChart,
  PrintFileBreakdownChart,
  EmptyState,
  useQAReportStats,
  useQADemandStats,
  calculateTrend,
  DateRange,
  Granularity,
} from '../';

type CompareMode = 'none' | 'previous_period';

// Default to last 7 days
const getDefaultDateRange = (): DateRange => ({
  from: subDays(new Date(), 7),
  to: new Date(),
});

// Compact one-line label for a date range, used as a card subtitle so the
// sum on the card never reads ambiguously. Single-day ranges collapse to
// just the day ("Apr 23, 2026") instead of "Apr 23 – Apr 23".
const formatRangeLabel = (range: DateRange): string => {
  const from = format(range.from, 'MMM d, yyyy');
  const to = format(range.to, 'MMM d, yyyy');
  return from === to ? from : `${from} to ${to}`;
};

export function QAReportsPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const { isStationUser } = useRole();
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange);
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [compareMode, setCompareMode] = useState<CompareMode>('none');

  // Calculate comparison date range (same duration, ending the day before primary starts)
  const comparisonRange = useMemo((): DateRange => {
    const durationDays = differenceInDays(dateRange.to, dateRange.from);
    const compEnd = subDays(dateRange.from, 1);
    const compStart = subDays(compEnd, durationDays);
    return { from: compStart, to: compEnd };
  }, [dateRange]);

  // Fetch primary data
  const { data, isLoading, error } = useQAReportStats(
    selectedLocation?.id,
    dateRange,
    granularity
  );

  // Fetch comparison data (only when comparison is enabled).
  // Comparison period is always historical — cache aggressively and never poll.
  const { data: comparisonData } = useQAReportStats(
    selectedLocation?.id,
    comparisonRange,
    granularity,
    compareMode !== 'none',
    { isHistorical: true }
  );

  // Fetch historical performance data (reuses same date range)
  const { data: historyData, isLoading: isLoadingHistory } = useDashboardHistory(
    selectedLocation?.id,
    dateRange
  );

  // Fetch Shopify demand (orders placed + units ordered) for the capacity card.
  const { data: demandData, isLoading: isLoadingDemand } = useQADemandStats(
    selectedLocation?.id,
    dateRange
  );

  // Fetch comparison history data (for prints delta).
  // Always historical — skip polling, cache aggressively.
  const { data: comparisonHistoryData } = useDashboardHistory(
    compareMode !== 'none' ? selectedLocation?.id : undefined,
    comparisonRange,
    { isHistorical: true }
  );

  // Human-readable labels for the selected + comparison date ranges.
  // Same-day ranges render as a single date ("Apr 23, 2026") so the card
  // subtitle doesn't read "Apr 23 – Apr 23".
  const rangeLabel = useMemo(
    () => formatRangeLabel(dateRange),
    [dateRange]
  );
  const comparisonRangeLabel = useMemo(
    () => formatRangeLabel(comparisonRange),
    [comparisonRange]
  );

  // Calculate total prints from history data
  const totalPrints = useMemo(() => {
    if (!historyData?.history) return 0;
    return historyData.history.reduce((sum, d) => sum + (d.garmentsPrinted ?? 0), 0);
  }, [historyData]);

  const comparisonTotalPrints = useMemo(() => {
    if (!comparisonHistoryData?.history) return undefined;
    return comparisonHistoryData.history.reduce((sum, d) => sum + (d.garmentsPrinted ?? 0), 0);
  }, [comparisonHistoryData]);

  // Orders fulfilled (shipped) in the window — the throughput side of the
  // capacity card, paired with demand (orders placed) from the demand query.
  const ordersFulfilled = useMemo(() => {
    if (!historyData?.history) return 0;
    return historyData.history.reduce((sum, d) => sum + (d.ordersShipped ?? 0), 0);
  }, [historyData]);

  // Daily prints data sorted by most recent first (for table)
  const dailyPrints = useMemo(() => {
    if (!historyData?.history) return [];
    return historyData.history
      .filter(d => d.garmentsPrinted > 0)
      .slice()
      .reverse();
  }, [historyData]);

  // Calculate trend for summary cards — uses comparison data when comparison mode is active
  const trend = useMemo(
    () => calculateTrend(data, compareMode !== 'none' ? comparisonData : undefined),
    [data, comparisonData, compareMode]
  );

  // Handle granularity change
  const handleGranularityChange = (value: string) => {
    setGranularity(value as Granularity);
  };

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8 space-y-6 animate-enter">
      {/* Page header with icon box pattern */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <BarChart3 className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">{t('qa.pageTitle')}</h1>
            <p className="text-muted-foreground">
              {t('qa.pageDescription')}
            </p>
          </div>
        </div>
        {!isStationUser() && (
          <Button variant="outline" asChild>
            <Link to="/production/reports/tv">
              <Maximize2 className="h-4 w-4 mr-2" aria-hidden="true" />
              {t('qa.tvMode')}
            </Link>
          </Button>
        )}
      </div>

      {/* Controls Row - Responsive */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Left side: Date picker + Granularity + Comparison */}
          <div className="flex flex-col sm:flex-row gap-2">
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              timezone={selectedLocation?.timezone}
            />
            <Select value={granularity} onValueChange={handleGranularityChange}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder={t('qa.granularity')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">{t('qa.granularity.day')}</SelectItem>
                <SelectItem value="week">{t('qa.granularity.week')}</SelectItem>
                <SelectItem value="month">{t('qa.granularity.month')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={compareMode} onValueChange={(v) => setCompareMode(v as CompareMode)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('qa.compare.none')}</SelectItem>
                <SelectItem value="previous_period">{t('qa.compare.previousPeriod')}</SelectItem>
              </SelectContent>
            </Select>
            {compareMode !== 'none' &&
              comparisonData !== undefined &&
              (comparisonData.summaryStats.totalProcessed ?? 0) === 0 &&
              (comparisonData.summaryStats.totalOrders ?? 0) === 0 && (
                <Badge
                  variant="outline"
                  className="border-amber-500/50 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400"
                >
                  {t('qa.compare.noHistory')}
                </Badge>
              )}
          </div>

          {/* Right side: Location indicator */}
          {selectedLocation && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>{t('qa.showingDataFor')} <strong className="text-foreground">{selectedLocation.name}</strong></span>
            </div>
          )}
        </div>
      </div>

      {/* No Location Selected State */}
      {!selectedLocation && (
        <Card>
          <CardContent className="py-12">
            <EmptyState
              icon={Building2}
              title={t('qa.noLocationTitle')}
              description={t('qa.noLocationDescription')}
            />
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {selectedLocation && error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            {t('qa.errorLoadingData')}
          </p>
        </div>
      )}

      {/* Main Content - Only show when location is selected */}
      {selectedLocation && (
        <>
          {/* Summary Stats Cards */}
          <SummaryStatsCards
            stats={data?.summaryStats ?? { totalDefects: 0, totalProcessed: 0, openIssues: 0, totalOrders: 0, printFileIssues: 0 }}
            trend={trend}
            isLoading={isLoading}
            comparisonStats={compareMode !== 'none' ? comparisonData?.summaryStats : undefined}
            totalPrints={totalPrints}
            comparisonTotalPrints={compareMode !== 'none' ? comparisonTotalPrints : undefined}
            dateRangeLabel={rangeLabel}
            comparisonRangeLabel={compareMode !== 'none' ? comparisonRangeLabel : undefined}
            capacity={{
              ordersPlaced: demandData?.ordersPlaced ?? 0,
              ordersFulfilled,
              unitsOrdered: demandData?.unitsOrdered ?? 0,
              unitsPrinted: totalPrints,
              isLoading: isLoadingDemand || isLoadingHistory,
            }}
          />

          {/* Historical Performance Chart - Full Width */}
          <Card>
            <CardHeader>
              <CardTitle>{t('qa.historicalPerformance')}</CardTitle>
              <CardDescription>{t('qa.historicalPerformanceDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <DashboardHistoryChart
                data={historyData?.history ?? []}
                isLoading={isLoadingHistory}
                dateRangeLabel={rangeLabel}
              />
            </CardContent>
          </Card>

          {/* Charts Section - Two Column on Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DefectTrendChart
              data={data?.defectTrend ?? []}
              granularity={granularity}
              isLoading={isLoading}
            />
            <DefectBreakdownChart
              data={data?.defectBreakdown ?? []}
              isLoading={isLoading}
            />
          </div>

          {/* Print Metrics Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <PrintFileBreakdownChart
              data={data?.printFileBreakdown ?? []}
              isLoading={isLoading}
            />

            {/* Daily Prints Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Printer className="h-5 w-5 text-orange-500" />
                  <div>
                    <CardTitle>{t('qa.dailyPrints')}</CardTitle>
                    <CardDescription>{t('qa.dailyPrintsDesc')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingHistory ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-8 bg-muted rounded animate-pulse" />
                    ))}
                  </div>
                ) : dailyPrints.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {t('qa.noPrintsRecorded')}
                  </p>
                ) : (
                  <div className="max-h-[320px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('qa.dateColumn')}</TableHead>
                          <TableHead className="text-right">{t('qa.printsColumn')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dailyPrints.map((day) => {
                          const parsed = parseISO(day.date);
                          const dateLabel = isValid(parsed) ? format(parsed, 'EEE, MMM d') : day.date;
                          return (
                            <TableRow key={day.date}>
                              <TableCell className="font-medium">{dateLabel}</TableCell>
                              <TableCell className="text-right tabular-nums">
                                {day.garmentsPrinted.toLocaleString()}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
